import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Calendar, CheckCircle, CreditCard, Loader2, MapPin, User } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PaymentProviderModal } from "@/components/booking/PaymentProviderModal";
import { StripeCheckoutModal } from "@/components/booking/StripeCheckoutModal";
import SpliceLogo from "@/components/icons/SpliceLogo";
import OpenGraphTags from "@/components/seo/OpenGraphTags";
import { buildAppointmentOpenGraph } from "@/lib/openGraph";
import {
  confirmStripeAppointmentPayment,
  initiateAppointmentDepositPayment,
  initiateAppointmentFullPayment,
  type PublicAppointmentSummary,
  type PublicAppointmentSummaryAppointment,
  usePublicAppointmentSummary,
} from "@/lib/public-appointments";
import {
  getPaymentOptionConfig,
  getPaymentOptionsForCountry,
  type PaymentOption,
  type PaymentProvider,
} from "@/lib/payment-providers";
import { formatPrice, type CurrencyDisplay } from "@/lib/utils";
import { bookingSiteOrigin } from "@/lib/siteUrl";
import { formatScheduledWall } from "@/lib/salon-time";

type PublicAppointmentVariant = "summary" | "payment" | "receipt";
type PayAction = "deposit" | "balance";

type PaystackCheckoutOptions = {
  key: string;
  email: string;
  amount: number;
  currency?: string;
  accessCode: string;
  channels?: string[];
  onSuccess: (transaction: { reference: string }) => void;
  onCancel: () => void;
};

type PaystackPopConstructor = new () => {
  checkout: (options: PaystackCheckoutOptions) => Promise<void> | void;
};

type PocketCheckoutConfig = {
  key: string;
  business_id?: string;
  wallet_id?: string;
  amount: number;
  reference: string;
  narration: string;
  view: "popup";
  account_expiry: number;
  meta: Record<string, unknown>;
  onSuccess: (response?: { reference?: string }) => void;
  onClose: () => void;
  onPending: () => void;
  onError: (error?: { message?: string }) => void;
};

type PocketConstructor = new (config: PocketCheckoutConfig) => { show: () => void };

const DEFAULT_CURRENCY: CurrencyDisplay = {
  symbol: "₦",
  code: "NGN",
  factor: 100,
};

function browserPaymentSdk() {
  return window as Window & {
    PaystackPop?: PaystackPopConstructor;
    Pocket?: PocketConstructor;
  };
}

function getCurrency(appointment?: PublicAppointmentSummaryAppointment | null): CurrencyDisplay {
  const currency = appointment?.salon?.business?.country?.currency;
  return {
    symbol: currency?.symbol || DEFAULT_CURRENCY.symbol,
    code: currency?.code || DEFAULT_CURRENCY.code,
    factor: currency?.factor || DEFAULT_CURRENCY.factor,
  };
}

function formatDate(value?: string | null) {
  if (!value) return "-";
  return formatScheduledWall(value, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  }) || "-";
}

function formatTime(value?: string | null) {
  if (!value) return "";
  return formatScheduledWall(value, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatStatus(status?: string | null) {
  return (status || "pending").replace(/_/g, " ");
}

function validateEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function appointmentAddress(appointment?: PublicAppointmentSummaryAppointment | null) {
  if (!appointment) return "";
  if (appointment.locationType === "home" && appointment.address) return appointment.address;

  return [
    appointment.salon?.address,
    appointment.salon?.city,
    appointment.salon?.state,
  ]
    .filter(Boolean)
    .join(", ");
}

function calculatePaidAmount(appointment?: PublicAppointmentSummaryAppointment | null) {
  if (!appointment?.payments?.length) return 0;

  return appointment.payments.reduce((sum, payment) => {
    return sum + (appointment.whoBearsProcessingFee === "salon" ? payment.totalAmount : payment.amount);
  }, 0);
}

function paymentMethodCountry(appointment?: PublicAppointmentSummaryAppointment | null) {
  return appointment?.salon?.countryCode || appointment?.salon?.business?.countryCode;
}

function fullName(appointment?: PublicAppointmentSummaryAppointment | null) {
  return [appointment?.client?.firstName, appointment?.client?.lastName].filter(Boolean).join(" ") || "Client";
}

function totalPayable(appointment?: PublicAppointmentSummaryAppointment | null) {
  return appointment?.actualTotalPaid || appointment?.totalPaid || appointment?.totalPrice || 0;
}

function usePaymentSdkScripts() {
  useEffect(() => {
    if (typeof document === "undefined") return;

    if (!document.querySelector('script[src="https://js.paystack.co/v2/inline.js"]')) {
      const script = document.createElement("script");
      script.src = "https://js.paystack.co/v2/inline.js";
      script.async = true;
      document.body.appendChild(script);
    }

    if (!document.querySelector('script[src="https://pocket-checkout-sdk.netlify.app/checkout-sdk.js"]')) {
      const script = document.createElement("script");
      script.src = "https://pocket-checkout-sdk.netlify.app/checkout-sdk.js";
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);
}

function DetailRow({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <span className={strong ? "font-semibold text-grey-900" : "text-grey-600"}>{label}</span>
      <span className={strong ? "font-semibold text-grey-900" : "text-grey-900"}>{value}</span>
    </div>
  );
}

function renderPolicyNode(node: ChildNode, key: string): ReactNode {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent;
  if (node.nodeType !== Node.ELEMENT_NODE) return null;

  const element = node as HTMLElement;
  const children = Array.from(element.childNodes).map((child, index) =>
    renderPolicyNode(child, `${key}-${index}`),
  );

  switch (element.tagName.toLowerCase()) {
    case "br":
      return <br key={key} />;
    case "p":
      return (
        <p key={key} className="mb-3 last:mb-0">
          {children}
        </p>
      );
    case "strong":
    case "b":
      return (
        <strong key={key} className="font-semibold">
          {children}
        </strong>
      );
    case "em":
    case "i":
      return <em key={key}>{children}</em>;
    case "u":
      return <span key={key} className="underline">{children}</span>;
    case "ul":
      return (
        <ul key={key} className="mb-3 list-disc space-y-1 pl-5 last:mb-0">
          {children}
        </ul>
      );
    case "ol":
      return (
        <ol key={key} className="mb-3 list-decimal space-y-1 pl-5 last:mb-0">
          {children}
        </ol>
      );
    case "li":
      return <li key={key}>{children}</li>;
    case "a": {
      const href = element.getAttribute("href") || "";
      const safeHref = /^(https?:|mailto:|tel:)/i.test(href) ? href : undefined;
      return safeHref ? (
        <a
          key={key}
          href={safeHref}
          target={safeHref.startsWith("http") ? "_blank" : undefined}
          rel={safeHref.startsWith("http") ? "noreferrer" : undefined}
          className="font-medium underline underline-offset-2"
        >
          {children}
        </a>
      ) : (
        <span key={key}>{children}</span>
      );
    }
    default:
      return <span key={key}>{children}</span>;
  }
}

function BookingPolicyContent({ html }: { html: string }) {
  const content = useMemo(() => {
    if (typeof window === "undefined") return html;
    const document = new DOMParser().parseFromString(html, "text/html");
    return Array.from(document.body.childNodes).map((node, index) =>
      renderPolicyNode(node, `policy-${index}`),
    );
  }, [html]);

  return <div className="text-sm leading-6 text-orange-900">{content}</div>;
}

function PaymentSuccessOverlay({
  action,
  appointment,
  onViewReceipt,
  onClose,
}: {
  action: PayAction;
  appointment?: PublicAppointmentSummaryAppointment | null;
  onViewReceipt: () => void;
  onClose: () => void;
}) {
  const businessName = appointment?.salon?.business?.name || "this business";
  const message =
    action === "deposit"
      ? `Your appointment at ${businessName} is confirmed upon deposit payment. A confirmation email or SMS will be sent shortly.`
      : `Your payment for your appointment at ${businessName} is being processed.`;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-xl">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-50">
          <CheckCircle className="h-9 w-9 text-green-600" />
        </div>
        <h2 className="mb-2 text-xl font-semibold text-grey-900">Payment made</h2>
        <p className="mb-6 text-sm leading-6 text-grey-600">{message}</p>
        <div className="flex flex-col gap-3">
          <Button onClick={onViewReceipt} className="bg-green-300 hover:bg-green-400 text-white">
            View Receipt
          </Button>
          <Button variant="outline" onClick={onClose}>
            Back to Summary
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function PublicAppointmentPage({ variant }: { variant: PublicAppointmentVariant }) {
  usePaymentSdkScripts();

  const navigate = useNavigate();
  const { bookingId = "" } = useParams<{ bookingId: string }>();
  const { data: summary, loading, error, refetch } = usePublicAppointmentSummary(bookingId);
  const appointment = summary?.appointment;
  const currency = getCurrency(appointment);
  const address = appointmentAddress(appointment);
  const totalAmountPaid = useMemo(() => calculatePaidAmount(appointment), [appointment]);
  const outstandingFee = useMemo(() => {
    const difference = totalPayable(appointment) - totalAmountPaid;
    return Number(Math.max(difference, 0).toFixed(2));
  }, [appointment, totalAmountPaid]);
  const appointmentInfoAmountLabel = totalAmountPaid > 0 && outstandingFee > 0 ? "Outstanding Balance" : "Amount";
  const appointmentInfoAmount = totalAmountPaid > 0 && outstandingFee > 0
    ? outstandingFee
    : appointment?.totalPaid || totalPayable(appointment);
  const priceHidden = variant !== "receipt" && Boolean(appointment?.salon?.business?.priceHidden);
  const clientHasEmail = Boolean(appointment?.client?.email);
  const [clientEmail, setClientEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [pendingPayAction, setPendingPayAction] = useState<PayAction | null>(null);
  const [processingAction, setProcessingAction] = useState<PayAction | null>(null);
  const [completedAction, setCompletedAction] = useState<PayAction | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null);
  const [stripeReference, setStripeReference] = useState<string | null>(null);
  const [stripeAppointmentId, setStripeAppointmentId] = useState<string | null>(null);
  const isCancelled = appointment?.appointmentStatus === "cancelled";
  const isCompleted = appointment?.appointmentStatus === "completed";
  const canInitiatePayment = clientHasEmail || validateEmail(clientEmail);
  const canPayDeposit = !isCancelled && totalAmountPaid === 0 && (appointment?.depositAmount || 0) > 0;
  const canPayBalance = !isCancelled && (appointment?.totalPaid || 0) > 0 && outstandingFee > 0;
  const showPaymentControls = variant !== "receipt" && (canPayDeposit || canPayBalance);
  const availablePaymentOptions = getPaymentOptionsForCountry(paymentMethodCountry(appointment));
  const paymentEmail = appointment?.client?.email || clientEmail;
  const hasPaymentDue =
    !isCancelled && ((summary?.amountToPay || 0) > 0 || canPayDeposit || canPayBalance || outstandingFee > 0);
  const origin = bookingSiteOrigin();
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "/b";
  const openGraph = buildAppointmentOpenGraph({ origin, pathname });

  const handleEmailChange = (email: string) => {
    setClientEmail(email);
    setEmailError(email && !validateEmail(email) ? "Please enter a valid email address" : "");
  };

  const handlePaymentSuccess = async (action: PayAction, reference?: string) => {
    setProcessingAction(null);
    setCompletedAction(action);
    await refetch();
    setShowSuccess(true);
    toast.success(reference ? "Payment submitted successfully" : "Payment submitted");
  };

  const handlePaymentClosed = () => {
    setProcessingAction(null);
  };

  const initiatePaystack = (
    paymentData: PublicAppointmentSummary,
    action: PayAction,
    channels?: string[],
  ) => {
    const PaystackPop = browserPaymentSdk().PaystackPop;
    const accessCode = paymentData.paymentAuthorization?.accessCode;
    const paystackKey = import.meta.env.VITE_PAYSTACK_PUBLIC_KEY || "";

    if (!PaystackPop || !accessCode || !paystackKey) {
      toast.error("Payment system is not ready. Please try again.");
      setProcessingAction(null);
      return;
    }

    const paystack = new PaystackPop();
    void paystack.checkout({
      key: paystackKey,
      email: paymentEmail,
      amount: Math.round((paymentData.amountToPay || 0) * currency.factor),
      currency: currency.code,
      accessCode,
      ...(channels ? { channels } : {}),
      onSuccess: (transaction: { reference: string }) => {
        void handlePaymentSuccess(action, transaction.reference);
      },
      onCancel: handlePaymentClosed,
    });
  };

  const initiatePocket = (paymentData: PublicAppointmentSummary, action: PayAction) => {
    const Pocket = browserPaymentSdk().Pocket;
    const pocketKey = import.meta.env.VITE_POCKET_PUBLIC_KEY || "";
    const sourceAppointment = paymentData.appointment || appointment;

    if (!Pocket || !pocketKey || !sourceAppointment) {
      toast.error("Payment system is not ready. Please try again.");
      setProcessingAction(null);
      return;
    }

    const reference =
      paymentData.paymentAuthorization?.reference ||
      `PKT-${sourceAppointment.id}-${Math.random().toString(36).slice(2, 9)}`;

    new Pocket({
      key: pocketKey,
      business_id: import.meta.env.VITE_POCKET_BUSINESS_ID,
      wallet_id: import.meta.env.VITE_POCKET_WALLET_ID,
      amount: Math.round((paymentData.amountToPay || 0) * currency.factor),
      reference,
      narration: `Payment for appointment at ${sourceAppointment.salon?.business?.name || "Splice"}`,
      view: "popup",
      account_expiry: 60,
      meta: {
        purpose: "appointment",
        appointment_id: sourceAppointment.id,
        business_id: sourceAppointment.salon?.business?.id,
        payment_type: action === "deposit" ? "deposit_payment" : "balance_payment",
        processing_fee_amount: paymentData.processingFeeAmount || 0,
      },
      onSuccess: (response) => {
        void handlePaymentSuccess(action, response?.reference || reference);
      },
      onPending: () => {
        void handlePaymentSuccess(action, reference);
      },
      onClose: handlePaymentClosed,
      onError: (pocketError) => {
        toast.error(pocketError?.message || "Payment failed. Please try again.");
        setProcessingAction(null);
      },
    }).show();
  };

  const initiateStripe = (paymentData: PublicAppointmentSummary) => {
    const clientSecret = paymentData.paymentAuthorization?.clientSecret;
    const reference = paymentData.paymentAuthorization?.reference || paymentData.paymentAuthorization?.accessCode;
    const sourceAppointment = paymentData.appointment || appointment;

    if (!clientSecret || !reference || !sourceAppointment?.id) {
      toast.error("Payment authorization failed. Please try again.");
      setProcessingAction(null);
      return;
    }

    setStripeClientSecret(clientSecret);
    setStripeReference(reference);
    setStripeAppointmentId(sourceAppointment.id);
  };

  const initiatePayment = (
    paymentData: PublicAppointmentSummary,
    provider: PaymentProvider,
    action: PayAction,
    channels?: string[],
  ) => {
    if (provider === "stripe") {
      initiateStripe(paymentData);
      return;
    }

    if (provider === "pocket") {
      initiatePocket(paymentData, action);
      return;
    }

    initiatePaystack(paymentData, action, channels);
  };

  const handleProviderSelect = async (option: PaymentOption) => {
    if (!appointment || !pendingPayAction) return;
    if (!canInitiatePayment) {
      setEmailError("Please enter a valid email address");
      return;
    }

    const action = pendingPayAction;
    const config = getPaymentOptionConfig(option, paymentMethodCountry(appointment));
    setShowProviderModal(false);
    setProcessingAction(action);

    try {
      const paymentData =
        action === "deposit"
          ? await initiateAppointmentDepositPayment({
              bookingId,
              email: clientHasEmail ? undefined : clientEmail,
              paymentProvider: config.provider,
              ...(config.channels ? { channels: config.channels } : {}),
            })
          : await initiateAppointmentFullPayment({
              id: bookingId,
              email: clientHasEmail ? undefined : clientEmail,
              paymentProvider: config.provider,
              ...(config.channels ? { channels: config.channels } : {}),
            });

      if (!paymentData) {
        throw new Error("Payment could not be initiated. Please try again.");
      }

      initiatePayment(paymentData, config.provider, action, config.channels);
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "Payment could not be initiated";
      toast.error(message);
      setProcessingAction(null);
    } finally {
      setPendingPayAction(null);
    }
  };

  const openProviderModal = (action: PayAction) => {
    if (!canInitiatePayment) {
      setEmailError("Please enter a valid email address");
      return;
    }
    setPendingPayAction(action);
    setShowProviderModal(true);
  };

  const handleStripeComplete = async () => {
    const action = processingAction || pendingPayAction || "balance";

    if (!stripeAppointmentId || !stripeReference) {
      toast.error("Payment completed, but could not be verified. Please contact support.");
      setProcessingAction(null);
      return;
    }

    try {
      await confirmStripeAppointmentPayment({
        appointmentId: stripeAppointmentId,
        reference: stripeReference,
      });
      setStripeClientSecret(null);
      setStripeReference(null);
      setStripeAppointmentId(null);
      await handlePaymentSuccess(action, stripeReference);
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "Payment could not be verified";
      toast.error(message);
      setProcessingAction(null);
    }
  };

  const handleStripeClose = () => {
    setStripeClientSecret(null);
    setStripeReference(null);
    setStripeAppointmentId(null);
    setProcessingAction(null);
  };

  const pageTitle =
    variant === "receipt"
      ? "Appointment receipt"
      : variant === "payment"
        ? isCancelled
          ? "Appointment cancelled"
          : canPayDeposit
            ? appointment?.salon?.business?.name
              ? `Make a deposit for your service at ${appointment.salon.business.name}`
              : "Make a deposit for your service"
            : hasPaymentDue
              ? "Complete your appointment payment"
              : isCompleted
                ? "Appointment completed"
                : "Appointment payment complete"
        : "Appointment summary";

  if (loading) {
    return (
      <>
        <OpenGraphTags {...openGraph} />
        <div className="flex min-h-screen items-center justify-center bg-grey-5">
          <div className="flex items-center gap-3 text-grey-600">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading appointment...
          </div>
        </div>
      </>
    );
  }

  if (error || !appointment) {
    return (
      <>
        <OpenGraphTags {...openGraph} />
        <div className="flex min-h-screen items-center justify-center bg-grey-5 px-4">
          <div className="max-w-md rounded-xl border border-grey-100 bg-white p-6 text-center shadow-sm">
            <h1 className="mb-2 text-xl font-semibold text-grey-900">Appointment not found</h1>
            <p className="text-sm text-grey-600">
              {error?.message || "Please check the link and try again."}
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <OpenGraphTags {...openGraph} />
      <main className="min-h-screen bg-grey-5">
        <header className="border-b border-grey-100 bg-white">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 py-5">
              <span className="text-sm text-grey-600">Powered by</span>
              <SpliceLogo className="h-7 w-auto" title="Splice" />
            </div>
            <div className="flex items-center justify-between border-t border-grey-100 py-6">
              <h1 className="text-2xl font-semibold text-grey-900">{pageTitle}</h1>
              <span className="rounded-full bg-grey-50 px-3 py-1 text-sm capitalize text-grey-700">
                {formatStatus(appointment.appointmentStatus)}
              </span>
            </div>
          </div>
        </header>

        <section className="mx-auto grid max-w-6xl gap-6 px-4 py-6 sm:px-6 lg:grid-cols-[1fr_420px] lg:px-8">
          <div className="space-y-6">
            <div className="rounded-2xl border border-grey-100 bg-white p-5 shadow-sm sm:p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  {appointment.salon?.business?.logoUrl ? (
                    <img
                      src={appointment.salon.business.logoUrl}
                      alt={`${appointment.salon.business.name || "Business"} logo`}
                      className="mb-4 h-14 w-14 rounded-lg object-contain"
                    />
                  ) : null}
                  <h2 className="text-xl font-semibold text-grey-900">
                    {appointment.salon?.business?.name}
                  </h2>
                  <p className="mt-1 text-sm text-grey-600">Booking ID: #{appointment.bookingId}</p>
                </div>
                {appointment.salon?.business?.headerImageUrl && appointment.salon.business.headerImageUrl !== appointment.salon.business.logoUrl ? (
                  <img
                    src={appointment.salon.business.headerImageUrl}
                    alt="Business header"
                    className="hidden h-20 w-20 rounded-xl object-cover sm:block"
                  />
                ) : null}
              </div>

              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl bg-grey-25 p-4">
                  <User className="mb-2 h-5 w-5 text-grey-500" />
                  <p className="text-xs font-medium uppercase tracking-wide text-grey-400">Who</p>
                  <p className="mt-1 text-sm font-medium text-grey-900">{fullName(appointment)}</p>
                </div>
                <div className="rounded-xl bg-grey-25 p-4">
                  <Calendar className="mb-2 h-5 w-5 text-grey-500" />
                  <p className="text-xs font-medium uppercase tracking-wide text-grey-400">When</p>
                  <p className="mt-1 text-sm font-medium text-grey-900">
                    {formatDate(appointment.startAt)} ・ {formatTime(appointment.startAt)}
                  </p>
                </div>
                <div className="rounded-xl bg-grey-25 p-4">
                  <MapPin className="mb-2 h-5 w-5 text-grey-500" />
                  <p className="text-xs font-medium uppercase tracking-wide text-grey-400">Where</p>
                  {address ? (
                    <a
                      href={`https://maps.google.com/?q=${encodeURIComponent(address)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-1 block text-sm font-medium text-green-700 underline-offset-2 hover:underline"
                    >
                      {address}
                    </a>
                  ) : (
                    <p className="mt-1 text-sm font-medium text-grey-900">Location unavailable</p>
                  )}
                </div>
                {!priceHidden ? (
                  <div className="rounded-xl bg-grey-25 p-4">
                    <CreditCard className="mb-2 h-5 w-5 text-grey-500" />
                    <p className="text-xs font-medium uppercase tracking-wide text-grey-400">{appointmentInfoAmountLabel}</p>
                    <p className="mt-1 text-sm font-medium text-grey-900">
                      {formatPrice(appointmentInfoAmount, currency)}
                    </p>
                  </div>
                ) : null}
              </div>
            </div>

            {showPaymentControls ? (
              <div className="rounded-2xl border border-grey-100 bg-white p-5 shadow-sm sm:p-6">
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-50">
                    <CreditCard className="h-5 w-5 text-green-700" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-grey-900">Payment</h2>
                    <p className="text-sm text-grey-600">
                      {canPayDeposit ? "Pay your deposit to secure this booking." : "Pay the remaining appointment balance."}
                    </p>
                  </div>
                </div>

                {!clientHasEmail ? (
                  <div className="mb-4">
                    <label className="mb-2 block text-sm font-medium text-grey-700" htmlFor="payment-email">
                      Email address
                    </label>
                    <input
                      id="payment-email"
                      type="email"
                      value={clientEmail}
                      onChange={(event) => handleEmailChange(event.target.value)}
                      placeholder="Enter your email address"
                      className="w-full rounded-lg border border-grey-200 px-4 py-3 text-sm outline-none transition focus:border-green-300 focus:ring-2 focus:ring-green-100"
                    />
                    {emailError ? <p className="mt-2 text-sm text-red-600">{emailError}</p> : null}
                  </div>
                ) : null}

                <div className="flex flex-col gap-3 sm:flex-row">
                  {canPayDeposit ? (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={!canInitiatePayment || Boolean(processingAction)}
                      onClick={() => openProviderModal("deposit")}
                      className="flex-1 border-green-300 text-green-700 hover:bg-green-50"
                    >
                      {processingAction === "deposit" ? "Processing..." : "Pay Deposit"}
                    </Button>
                  ) : null}
                  {canPayBalance ? (
                    <Button
                      type="button"
                      disabled={!canInitiatePayment || Boolean(processingAction)}
                      onClick={() => openProviderModal("balance")}
                      className="flex-1 bg-green-300 text-white hover:bg-green-400"
                    >
                      {processingAction === "balance"
                        ? "Processing..."
                        : totalAmountPaid > 0
                          ? "Pay Outstanding Balance"
                          : "Pay in Full"}
                    </Button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {appointment.salon?.business?.cancellationPolicy ? (
              <div className="rounded-2xl border border-orange-100 bg-orange-50 p-5 text-sm leading-6 text-orange-900">
                <p className="font-semibold">Booking Policy</p>
                <div className="mt-2">
                  <BookingPolicyContent html={appointment.salon.business.cancellationPolicy} />
                </div>
              </div>
            ) : null}
          </div>

          <aside className="rounded-2xl border border-grey-100 bg-white p-5 shadow-sm sm:p-6 lg:sticky lg:top-6 lg:self-start">
            <h2 className="text-lg font-semibold text-grey-900">
              {variant === "receipt" ? "Receipt" : "Details"}
            </h2>

            <div className="mt-5 space-y-1">
              <div className="mb-2 flex items-center justify-between rounded-lg bg-grey-50 px-3 py-2 text-sm font-medium text-grey-600">
                <span>Item</span>
                {!priceHidden ? <span>Amount</span> : <span>Time</span>}
              </div>

              {appointment.appointmentServices?.map((service) => (
                <DetailRow
                  key={service.id}
                  label={`${service.name || "Service"}${service.quantity && service.quantity > 1 ? ` x${service.quantity}` : ""}`}
                  value={
                    priceHidden
                      ? formatTime(service.startAt)
                      : formatPrice((service.price || 0) * (service.quantity || 1), currency)
                  }
                />
              ))}

              {appointment.appointmentProducts?.map((product) => (
                <DetailRow
                  key={product.id}
                  label={`${product.name || "Product"}${product.quantity && product.quantity > 1 ? ` x${product.quantity}` : ""}`}
                  value={formatPrice((product.price || 0) * (product.quantity || 1), currency)}
                />
              ))}

              {!priceHidden ? (
                <>
                  <div className="my-3 border-t border-grey-100" />
                  <DetailRow
                    label="Subtotal"
                    value={formatPrice((appointment.totalServicesAmount || 0) + (appointment.totalProductsAmount || 0), currency)}
                  />
                  {appointment.salon?.isTaxVisible && appointment.taxAmount ? (
                    <DetailRow label="Tax" value={formatPrice(appointment.taxAmount, currency)} />
                  ) : null}
                  {appointment.discountAmount && appointment.discountAmount > 0 ? (
                    <DetailRow label="Discount" value={`-${formatPrice(appointment.discountAmount, currency)}`} />
                  ) : null}
                  {appointment.appointmentPromo?.amount && appointment.appointmentPromo.amount > 0 ? (
                    <DetailRow label="Promo Discount" value={`-${formatPrice(appointment.appointmentPromo.amount, currency)}`} />
                  ) : null}
                  {variant === "receipt" && appointment.processingFeeAmount ? (
                    <DetailRow label="Processing fee" value={formatPrice(appointment.processingFeeAmount, currency)} />
                  ) : null}
                  <div className="my-3 border-t border-grey-100" />
                  <DetailRow label="Total" value={formatPrice(totalPayable(appointment), currency)} strong />
                  {totalAmountPaid > 0 ? (
                    <DetailRow label="Amount paid" value={formatPrice(totalAmountPaid, currency)} />
                  ) : null}
                  {appointment.depositAmount > 0 && totalAmountPaid > 0 && totalAmountPaid < appointment.depositAmount ? (
                    <DetailRow
                      label="Deposit Balance"
                      value={formatPrice(appointment.depositAmount - totalAmountPaid, currency)}
                    />
                  ) : null}
                  {outstandingFee > 0 && totalAmountPaid > 0 ? (
                    <DetailRow label="Outstanding Balance" value={formatPrice(outstandingFee, currency)} strong />
                  ) : null}
                </>
              ) : null}
            </div>

            {variant === "receipt" && !isCancelled && outstandingFee > 0 ? (
              <Button
                type="button"
                onClick={() => navigate(`/b/${bookingId}`)}
                className="mt-6 w-full bg-green-300 text-white hover:bg-green-400"
              >
                {totalAmountPaid > 0 ? "Pay Outstanding Balance" : "Pay in Full"}
              </Button>
            ) : null}
          </aside>
        </section>

        {summary?.beautyNowAppUrl && summary.beautyNowAppCtaImageUrl ? (
          <div className="mx-auto max-w-6xl px-4 pb-8 sm:px-6 lg:px-8">
            <a href={summary.beautyNowAppUrl} target="_blank" rel="noreferrer" className="block max-w-lg">
              <img
                src={summary.beautyNowAppCtaImageUrl}
                alt="Download BeautyNow by Splice"
                className="w-full rounded-xl border border-grey-100"
              />
            </a>
          </div>
        ) : null}
      </main>

      <PaymentProviderModal
        isOpen={showProviderModal}
        onClose={() => {
          setShowProviderModal(false);
          setPendingPayAction(null);
        }}
        onSelectOption={handleProviderSelect}
        loading={Boolean(processingAction)}
        availableOptions={availablePaymentOptions}
      />

      <StripeCheckoutModal
        isOpen={Boolean(stripeClientSecret)}
        clientSecret={stripeClientSecret}
        onClose={handleStripeClose}
        onComplete={handleStripeComplete}
      />

      {showSuccess && completedAction ? (
        <PaymentSuccessOverlay
          action={completedAction}
          appointment={appointment}
          onViewReceipt={() => navigate(`/receipt/${bookingId}`)}
          onClose={() => setShowSuccess(false)}
        />
      ) : null}
    </>
  );
}
